import { apiSlice } from '../../store/api/apiSlice';
import { ENV_CONFIG } from '../../shared/config/env.config';

// ── Response/entity shapes (from the Angular SchoolService responses) ────────
export interface ClassItem {
  class_studying: string;
  class_id: number;
}

export interface SectionItem {
  section: string;
}

export interface MediumItem {
  medium_name: string;
  group_name: string;
  group_id: number | string;
  school_medium_id: number | string;
  // present on the target medium payload
  medium_name_1?: string;
}

export interface StudentItem {
  id: number | string;
  user_id: string;
  name: string;
  school_id: number | string;
}

interface ResultResponse<T> {
  result: T[];
}

export interface PromoteResponse {
  dataStatus: boolean;
  message?: string;
}

// Section lookup body: { records: { class_id, school_id } }
export interface SectionParams {
  records: { class_id: number | string; school_id: number | string };
}

export interface MediumParams {
  class_id: number | string;
  section: string;
  school_id: number | string;
}

export interface StudentListParams {
  class_studying_id: number | string;
  class_section: string;
  school_id: number | string;
}

export interface PromoteParams {
  records: {
    update_list: Record<string, unknown>[];
    insert_list: Record<string, unknown>[];
  };
}

// The Angular getSchoolClassDetails() hits Django on LOCAL/STAGING and the TNTP
// PHP repo on PRODUCTION; everything else in this module goes to the TNTP repo
// (dataService.getDataTntp / postTntp → apiBaseUrlTntp).
const isProduction = ENV_CONFIG.environment === 'PRODUCTION';

export const studentPromotionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // getSchoolClassDetails(schlId)
    getSchoolClasses: builder.query<
      ResultResponse<ClassItem>,
      { schoolId: number | string }
    >({
      query: ({ schoolId }) =>
        isProduction
          ? {
              url: 'api/schoolWiseClassandSection',
              method: 'GET',
              params: { school_id: schoolId },
              service: 'phpTntp',
            }
          : {
              url: 'schoolWiseClassandSection',
              method: 'GET',
              params: { school_id: schoolId },
              service: 'djangoEmis',
            },
    }),

    // getSchoolSectionDetails(data) — POST /api/classWiseSection
    getSchoolSections: builder.mutation<
      ResultResponse<SectionItem>,
      SectionParams
    >({
      query: (data) => ({
        url: 'api/classWiseSection',
        method: 'POST',
        body: data,
        service: 'phpTntp',
      }),
    }),

    // getSchoolMediumList(classid, section, schlId) — GET /api/GetSectionMediumDetails/
    getSchoolMedium: builder.query<ResultResponse<MediumItem>, MediumParams>({
      query: ({ class_id, section, school_id }) => ({
        url: 'api/GetSectionMediumDetails/',
        method: 'GET',
        params: { class_id, section, school_id },
        service: 'phpTntp',
      }),
    }),

    // getStudentNameList(classid, section, schlId) — GET /api/GetListPromoteStudents/
    getStudentNames: builder.query<
      ResultResponse<StudentItem>,
      StudentListParams
    >({
      query: ({ class_studying_id, class_section, school_id }) => ({
        url: 'api/GetListPromoteStudents/',
        method: 'GET',
        params: { class_studying_id, class_section, school_id },
        service: 'phpTntp',
      }),
    }),

    // PromoteStudentList(data) — POST /api/UpdatePromoteStudents
    promoteStudents: builder.mutation<PromoteResponse, PromoteParams>({
      query: (data) => ({
        url: 'api/UpdatePromoteStudents',
        method: 'POST',
        body: data,
        service: 'phpTntp',
      }),
    }),
  }),
});

export const {
  useGetSchoolClassesQuery,
  useGetSchoolSectionsMutation,
  useLazyGetSchoolMediumQuery,
  useLazyGetStudentNamesQuery,
  usePromoteStudentsMutation,
} = studentPromotionApi;
